import { useState } from "react";
import {
  Scales, ArrowsCounterClockwise, ChatsCircle, CaretRight,
  PencilSimple, Trash, Check, X,
} from "@phosphor-icons/react";
import InlineRename from "./InlineRename";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function evalTitle(item) {
  if (item.display_name) return item.display_name;
  const gi = item.evaluation?.gathered_info || {};
  const parts = [gi.drawing_type, gi.city || gi.state].filter(Boolean);
  return parts.length > 0 ? parts.join(" — ") : "Drawing Evaluation";
}

function compTitle(item) {
  if (item.display_name) return item.display_name;
  const ref = item.reference_filename;
  const lat = item.latest_filename;
  if (ref && lat) return `${ref} → ${lat}`;
  if (ref) return `Reference: ${ref}`;
  return "Drawing Comparison";
}

export function getItemTitle(item) {
  if (item._type === "analysis") {
    if (item.display_name) return item.display_name;
    const drawings = item.drawings || [];
    if (drawings.length > 0) return drawings.map(d => d.filename).join(", ").slice(0, 50);
    return "Analysis Session";
  }
  return item._type === "comparison" ? compTitle(item) : evalTitle(item);
}

export default function HistoryRow({ item, onClick, onDelete, onRename }) {
  const isComp     = item._type === "comparison";
  const isAnalysis = item._type === "analysis";
  const evalData   = item.evaluation || {};
  const status     = evalData.overall_status;
  const issues     = evalData.issues?.length ?? 0;
  const changes    = item.changes_count ?? "—";
  const drawings   = item.drawings?.length ?? 0;
  const title      = getItemTitle(item);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming]           = useState(false);

  const handleDelete = (e) => {
    e.stopPropagation();
    if (confirmDelete) onDelete(item, e);
    else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setConfirmDelete(false);
    setRenaming(true);
  };

  const iconBg = isComp
    ? "bg-primary/10 text-primary"
    : isAnalysis
    ? "bg-green-100 text-green-700"
    : "bg-orange-100 text-orange-600";

  if (renaming) {
    return (
      <div className="w-full px-4 py-3 flex items-center gap-3" data-testid={`history-row-${item.id || item.session_id}`}>
        <div className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${iconBg}`}>
          {isComp ? <ArrowsCounterClockwise size={15} /> : isAnalysis ? <ChatsCircle size={15} /> : <Scales size={15} />}
        </div>
        <InlineRename
          initialValue={title}
          onSave={name => { onRename(item, name); setRenaming(false); }}
          onCancel={() => setRenaming(false)}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors group flex items-center gap-3 cursor-pointer"
      onClick={onClick}
      data-testid={`history-row-${item.id || item.session_id}`}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${iconBg}`}>
        {isComp ? <ArrowsCounterClockwise size={15} /> : isAnalysis ? <ChatsCircle size={15} /> : <Scales size={15} />}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
          {!isComp && !isAnalysis && status && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              status === "COMPLIANT" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}>{status}</span>
          )}
          {!isComp && !isAnalysis && (
            <span className="text-xs text-slate-400">{issues} issue{issues !== 1 ? "s" : ""}</span>
          )}
          {isComp && typeof changes === "number" && (
            <span className="text-xs text-slate-400">{changes} change{changes !== 1 ? "s" : ""}</span>
          )}
          {isAnalysis && (
            <span className="text-xs text-slate-400">{drawings} drawing{drawings !== 1 ? "s" : ""}</span>
          )}
        </div>
      </div>

      <div
        className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={e => e.stopPropagation()}
      >
        {confirmDelete ? (
          <>
            <span className="text-xs text-red-600 font-medium mr-1">Delete?</span>
            <button onClick={handleDelete} className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors" title="Confirm delete" data-testid={`confirm-delete-${item.id || item.session_id}`}>
              <Check size={13} weight="bold" />
            </button>
            <button onClick={e => { e.stopPropagation(); setConfirmDelete(false); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded transition-colors" title="Cancel" data-testid={`cancel-delete-${item.id || item.session_id}`}>
              <X size={13} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <button onClick={handleRenameClick} className="p-1 text-slate-400 hover:text-primary hover:bg-slate-100 rounded transition-colors" title="Rename" data-testid={`rename-btn-${item.id || item.session_id}`}>
              <PencilSimple size={13} />
            </button>
            <button onClick={handleDelete} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" data-testid={`delete-btn-${item.id || item.session_id}`}>
              <Trash size={13} />
            </button>
          </>
        )}
      </div>

      <CaretRight size={14} className="flex-shrink-0 text-slate-300 group-hover:text-slate-500 transition-colors" />
    </div>
  );
}
